import type { PortalAccountSummary, PortalCaseData, PortalMeData } from '~/types/portal'

/**
 * Raw $fetch calls against /api/client/{tenantSlug}/... — deliberately NOT
 * routed through the shared useApi()/plugins/api.ts machinery, which
 * attaches the STAFF Sanctum token and X-Tenant-Slug from the agent-side
 * tenant context. Portal accounts carry their own bearer token
 * (usePortalAuth) and the tenant slug is already in the URL path, so
 * mixing in the staff auth layer would be both wrong and a cross-auth risk.
 * Same rationale the original magic-link portal page documented for
 * bypassing repositories.
 */
export function usePortalAccountApi(tenantSlug: string) {
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBaseUrl
  const { token } = usePortalAuth(tenantSlug)

  const base = `${apiBase}/api/client/${tenantSlug}`

  function authHeaders(): Record<string, string> {
    return token.value ? { Authorization: `Bearer ${token.value}` } : {}
  }

  const register = (email: string, password: string, password_confirmation: string, fullName: string, locale?: string) =>
    $fetch<{ message: string }>(`${base}/auth/register`, {
      method: 'POST',
      body: { email, password, password_confirmation, full_name: fullName, locale }
    })

  const login = (email: string, password: string) =>
    $fetch<{ token: string, account: PortalAccountSummary }>(`${base}/auth/login`, {
      method: 'POST',
      body: { email, password }
    })

  const googleRedirectUrl = () =>
    $fetch<{ url: string }>(`${base}/auth/google/redirect`)

  const logout = () =>
    $fetch(`${base}/auth/logout`, { method: 'POST', headers: authHeaders() })

  const forgotPassword = (email: string) =>
    $fetch<{ message: string }>(`${base}/auth/forgot-password`, { method: 'POST', body: { email } })

  const resetPassword = (id: number, resetToken: string, password: string, password_confirmation: string) =>
    $fetch<{ data: PortalAccountSummary }>(`${base}/auth/reset-password`, {
      method: 'POST',
      body: { id, token: resetToken, password, password_confirmation }
    })

  const me = () =>
    $fetch<{ data: PortalMeData }>(`${base}/me`, { headers: authHeaders() })

  const showCase = (caseId: number) =>
    $fetch<{ data: PortalCaseData }>(`${base}/cases/${caseId}`, { headers: authHeaders() })

  const uploadToSlot = (caseId: number, slotId: number, files: File[]) => {
    const formData = new FormData()
    for (const f of files) formData.append('files[]', f)
    return $fetch<{ data: { failed: Array<{ name: string, reason: string }> } }>(
      `${base}/cases/${caseId}/slots/${slotId}/upload`,
      { method: 'POST', body: formData, headers: authHeaders() }
    )
  }

  const downloadFile = (caseId: number, fileId: number) =>
    $fetch<{ data: { url: string } }>(`${base}/cases/${caseId}/files/${fileId}/download`, { headers: authHeaders() })

  const deleteFile = (caseId: number, fileId: number) =>
    $fetch(`${base}/cases/${caseId}/files/${fileId}`, { method: 'DELETE', headers: authHeaders() })

  const postMessage = (caseId: number, body: string) =>
    $fetch(`${base}/cases/${caseId}/messages`, { method: 'POST', body: { body }, headers: authHeaders() })

  const uploadReceipt = (caseId: number, paymentId: number, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return $fetch(`${base}/cases/${caseId}/payments/${paymentId}/receipt`, {
      method: 'POST',
      body: formData,
      headers: authHeaders()
    })
  }

  const acceptQuote = (caseId: number, quoteId: number) =>
    $fetch(`${base}/cases/${caseId}/quotes/${quoteId}/accept`, { method: 'POST', headers: authHeaders() })

  const declineQuote = (caseId: number, quoteId: number, reason?: string) =>
    $fetch(`${base}/cases/${caseId}/quotes/${quoteId}/decline`, {
      method: 'POST',
      body: { reason },
      headers: authHeaders()
    })

  return {
    register, login, logout, forgotPassword, resetPassword, googleRedirectUrl,
    me, showCase, uploadToSlot, downloadFile, deleteFile, postMessage, uploadReceipt,
    acceptQuote, declineQuote
  }
}
